import React, { useEffect, useRef, useState } from 'react';
import { CommonBackContent } from '../Layouts/CommonBackContent';
import dynamic from 'next/dynamic';
import { InstagramAnonymouslyModel } from '@/types/anonymously.interface';
import CommonSimpleModal from '@/components/Common/CommonSimpleModal';

import { useTranslation } from 'next-i18next';
import { fetchInstagramAnonymouslySearchData } from '@/lib/service/anonymously';

import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { message } from 'antd';
import { DownloadStoriesPreviewModel, InsAccountModel } from '@/types/home';
import { AccountLimitAlert, LimitAlert } from './FeeLimitAlert';
import { addInsStory } from '@/lib/service/dashboard';
import router from 'next/router';
import CustomersTalkUsContent from './CustomersTalkUsContent';
import UserDashboard from '../Dashboard/UserDashboard';
import useStoryPremium from '@/lib/hooks/isPremium/useStoryPremium';
import TurnstileModal from '@/components/TurnstileModal';
import useTurnstileVerify from '@/lib/hooks/anonymous-instagram-story-viewer/useTurnstileVerify';

const FAQContent = dynamic(() => import('./FAQContent'), { ssr: true });
const DownloadInstagramContent = dynamic(() => import('./DownloadInstagramContent'), { ssr: true });
const TopSearchContent = dynamic(() => import('./TopSearch'), { ssr: true });
// CLS 优化：骨架屏和 SearchResultContent 都使用静态导入，确保同帧切换
import SearchResultSkeleton from '@/components/web-viewer-for-instagram/SearchResultSkeleton';
import SearchResultContent from './SearchResultContent';
const ChooseContent = dynamic(() => import('./ChooseContent'), { ssr: true });
const GeneralExampDownloadContent = dynamic(() => import('./GeneralExampDownloadContent'), {
  ssr: true,
});
const WhyDownloadStoriesContent = dynamic(() => import('./WhyDownloadStoriesContent'), {
  ssr: true,
});
const InstagramActivityTrackerContent = dynamic(() => import('./InstagramActivityTracker'), {
  ssr: true,
});
const BestTipsContent = dynamic(() => import('./BestTipsContent'), { ssr: true });
const DownloadStoriesBottomContent = dynamic(() => import('./DownloadStoriesBottomContent'), {
  ssr: true,
});
const LandingPagePricingContent = dynamic(() => import('./PricingContent'), { ssr: true });

const DownloadInstagramStoryContent = ({ data }: { data?: any }) => {
  const [contentData, setContentData] = useState<DownloadStoriesPreviewModel>(data?.contentData);
  const [currentModel, setCurrentModel] = useState<InstagramAnonymouslyModel>(null);
  const searchResultRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);
  const { turnstileRef, getTurnstileToken } = useTurnstileVerify();
  const [alertInfo, setAlertInfo] = useState<any>(null);
  const { t } = useTranslation();
  const [errorCode, setErrorCode] = useState<string | number>('');
  const [showLimitPage, setShowLimitPage] = useState<boolean>(false);
  const [showAccountLimit, setShowAccountLimit] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [inputVisible, setInputVisible] = useState(true);
  const [insListLoading, setInsListLoading] = useState(false);
  const { isPremium } = useStoryPremium();
  // CLS 优化：showContent 控制骨架屏到内容的同帧切换
  const [showContent, setShowContent] = useState(false);
  // CLS 优化：弹窗显示期间保持骨架屏
  const [keepSkeletonOnError, setKeepSkeletonOnError] = useState(false);

  useEffect(() => {
    if (showLimitPage) {
      ga_logEvent(EventName.GA_search_now_limit_notify_displayed);
    }
  }, [showLimitPage]);

  useEffect(() => {
    setContentData(data?.contentData);
  }, [data]);

  const onValueChanged = (e?: string) => {
    setSearchStr(e ?? '');
  };

  const onSearch = async (e?: string): Promise<void> => {
    if (e !== searchStr && !currentModel) {
      setCurrentModel(null);
    }
    setSearchStr(e ?? '');

    // 增加未登录免费试用后，搜索时不再检查登录状态
    if (loading) return;

    const turnstileToken = await getTurnstileToken();
    if (turnstileToken === null) {
      return;
    }
    onSearchClick(e, turnstileToken);
  };

  const onSearchClick = (e?: string, token?: string) => {
    if (e?.length > 0) {
      if (loading) {
        return;
      }
      if (e.includes('/reel/') || e.includes('/reels/')) {
        setLoading(true);
        setKeepSkeletonOnError(true);
        setAlertInfo({
          title: t('Invalid Instagram Username'),
          desc: t('The username you entered is not valid. Please check the username and try again.'),
          btnTitle: t('TRYAGAIN'),
          type: 'Instagram-Account-Not-Found',
        });
        return;
      }
      setLoading(true);
      // CLS 优化：搜索开始时重置 showContent，显示骨架屏预占位
      setShowContent(false);

      fetchInstagramAnonymouslySearchData(e, token ?? '', null, {
        isPremium,
        bizFuncName: 'stories_viewer',
      })
        .then((res) => {
          if (res?.code === 0) {
            if (res.data.private_mark) {
              let PrivateData: InsAccountModel = {
                is_private: res.data.private_mark ? 1 : 0,
                profile_pic_url: res.data.avatar,
                media_name: res.data.media_name,
              };
              // CLS 优化：私密账户弹窗期间保持骨架屏
              setCurrentModel(res?.data);
              setKeepSkeletonOnError(true);
              setAlertInfo({
                title: t('OopsPrivateProfileDetected'),
                desc: (
                  <>
                    <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                      {t('ThisInstagramAccountIsPrivate_Desc')}
                    </div>
                    <UserDashboard {...PrivateData} />
                  </>
                ),
                btnTitle: t('TRYAGAIN'),
                btnDesc: (
                  <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
                    {t('Note: Only public accounts can be tracked.')}
                  </p>
                ),
              });
              return;
            }

            ga_logEvent(EventName.GA_one_click_displayed);

            // CLS 优化核心：doShowContent 将所有状态更新集中在一起
            // 根据 React 18 自动批处理机制，同一同步函数内的多个 setState 只触发一次渲染
            // 这样骨架屏消失和内容出现在同一帧内完成，不会产生 CLS
            const doShowContent = () => {
              setCurrentModel(res?.data);
              setLoading(false);
              setShowContent(true);
            };

            // 先滚动到目标位置，滚动完成后再显示内容
            // 这样即使骨架屏和内容高度有差异，CLS 也发生在视口边缘或外部
            if (searchResultRef.current) {
              const targetTop = searchResultRef.current.offsetTop - 220;
              window.scrollTo({
                top: targetTop,
                behavior: 'smooth',
              });
              // 等待滚动完成后再显示内容
              let scrollTimer: NodeJS.Timeout;
              const handleScrollEnd = () => {
                clearTimeout(scrollTimer);
                scrollTimer = setTimeout(() => {
                  window.removeEventListener('scroll', handleScrollEnd);
                  doShowContent();
                }, 100);
              };
              window.addEventListener('scroll', handleScrollEnd);
              handleScrollEnd(); // 立即检查一次，以防不需要滚动
            } else {
              doShowContent();
            }
          } else if (res?.code === 22001 || res?.code === 22006) {
            setShowLimitPage(true);
            // 错误时重置 loading，保留之前的 showContent 状态
            setLoading(false);
          } else if (res?.code === 22004 || res?.code === 22006) {
            // 隐私账号走这里
            setErrorCode(res?.code);
            setCurrentModel(null);
            setLoading(false);
          } else if (res?.code === 22002) {
            // CLS 优化：弹窗显示期间保持骨架屏
            setKeepSkeletonOnError(true);
            setAlertInfo({
              title: t('Invalid Instagram Username'),
              desc: t('The username you entered is not valid. Please check the username and try again.'),
              btnTitle: t('TRYAGAIN'),
              type: 'Instagram-Account-Not-Found',
            });
            setCurrentModel(null);
          } else {
            // CLS 优化：其他错误（如 Too many requests）也保持骨架屏显示，直到用户关闭弹窗
            setKeepSkeletonOnError(true);
            setAlertInfo({
              title: t('Warning'),
              desc: res?.message || t('An error occurred. Please try again later.'),
              btnTitle: t('OK'),
              type: 'API-Error',
            });
            // 注意：不调用 setLoading(false)，保持骨架屏显示
          }
        })
        .catch(() => {
          // CLS 优化：网络异常也保持骨架屏显示，直到用户关闭弹窗
          setKeepSkeletonOnError(true);
          setAlertInfo({
            title: t('Network Error'),
            desc: t('Network connection failed. Please check your internet connection and try again.'),
            btnTitle: t('OK'),
            type: 'Network-Error',
          });
          // 注意：不调用 setLoading(false)，保持骨架屏显示
        });
    } else {
      message.error(t('Please enter the insert link or username'));
    }
  };

  const onAddAccountToTrackClick = (media_name?: string) => {
    if (insListLoading) {
      return;
    }
    setInsListLoading(true);

    addInsStory(media_name)
      ?.then((res) => {
        if (res?.code === 0) {
          message.success(t('Added successfully'));
          router.push('/dashboard?dashboard_type=toolkit');
        } else {
          if (res?.code !== 22008) {
            message.error(res?.message);
          }

          if (res?.code === 22007) {
            setShowLimitPage(true);
          } else if (res?.code === 22008) {
            setShowAccountLimit(true);
          }
        }
      })
      .finally(() => {
        setInsListLoading(false);
      });
  };

  const gotoPlanClick = () => {
    if (priceRef.current) {
      const targetTop = priceRef.current.offsetTop - 60;
      window.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <CommonBackContent>
        <TopSearchContent
          topInfo={contentData?.contentInfo?.hero}
          loading={loading}
          searchVal={searchStr}
          onSearchClick={(e) => {
            onSearch(e);
            ga_logEvent(EventName.GA_search_now_clicks);
          }}
          onValueChanged={onValueChanged}
          onInputVisibleChanged={(visible) => setInputVisible(visible)}
          errorCode={errorCode}
          resetErrorCode={() => setErrorCode('')}
          btnPlaceholder={t('Download')}
        />

        {/* CLS 优化：loading 时显示骨架屏预占位，数据返回后同帧切换到真实内容 */}
        <div ref={searchResultRef}>
          {(loading || keepSkeletonOnError) && !showContent ? (
            <SearchResultSkeleton tabFunc={data?.contentData?.function_child ?? 'highlights'} />
          ) : showContent && currentModel ? (
            <SearchResultContent
              loading={insListLoading}
              loadMoreNotCheckLogin={true}
              tabFunc={data?.contentData?.function_child}
              post_page_token={currentModel?.post_page_token}
              data={currentModel}
              onAddAccountToTrackClick={onAddAccountToTrackClick}
              onPlanClick={() => setShowLimitPage(true)}
            />
          ) : null}
        </div>

        <DownloadInstagramContent data={contentData?.contentInfo['section-1']} />
        <ChooseContent data={contentData?.contentInfo['section-2']} />
        <GeneralExampDownloadContent data={contentData?.contentInfo['section-3']} />
        <WhyDownloadStoriesContent data={contentData?.contentInfo['section-4']} />
        <div ref={priceRef}>
          <LandingPagePricingContent func="story" />
        </div>
        <InstagramActivityTrackerContent
          contentData={contentData}
          isTracker={true}
          webBg={'https://res-front.pumpsoul.com/ins-prod/image/activity_tracker_bg_web_0110_1.webp'}
          phoneBg={'https://res-front.pumpsoul.com/ins-prod/image/activity_tracker_bg_phone_0109.webp'}
        />

        <CustomersTalkUsContent contentInfo={contentData?.contentInfo?.['section-user-reviews']} />

        {contentData?.faqList && (
          <FAQContent faqInfo={contentData?.contentInfo?.['section-faq']} faqList={contentData?.faqList} />
        )}
        <BestTipsContent
          title={contentData?.contentInfo?.['section-top-blog']?.title}
          blogList={contentData?.topBlogList}
        />
        <BestTipsContent title={contentData?.contentInfo?.['section-blog']?.title} blogList={contentData?.blogList} />
      </CommonBackContent>

      <DownloadStoriesBottomContent
        bottomInfo={contentData?.contentInfo['section-buttom']}
        loading={loading}
        searchVal={searchStr}
        inputVisible={inputVisible}
        onValueChanged={onValueChanged}
        onSearchClick={(e) => {
          window.scrollTo({ top: 220 });
          onSearch(e);
          ga_logEvent(EventName.GA_add_tracking_button_clicks);
        }}
        buttonTitle={t('Download')}
      />

      <CommonSimpleModal
        open={alertInfo !== null}
        title={alertInfo?.title}
        desc={alertInfo?.desc}
        btnTitle={alertInfo?.btnTitle}
        closeAction={(isOk) => {
          // CLS 优化：弹窗关闭时重置骨架屏状态
          if (keepSkeletonOnError) {
            setKeepSkeletonOnError(false);
            setLoading(false);
          }

          if (isOk && alertInfo?.href) {
            if (alertInfo.type == 'No-Stories-Detected') {
              setAlertInfo(null);
              router.push(
                `${router.asPath}?media_name=${alertInfo?.media_name}&avatar=${alertInfo?.avatar}&media_url=${alertInfo?.media_url}#1`
              );
              return;
            }
            router.push(alertInfo?.href);
          }
          if (isOk) {
            if ('Out-of-Views' === alertInfo?.type) {
              ga_logEvent(EventName.ViewInstagramStoriesAnonymously_OutOfViews_AddAccountNow);
            } else {
              setAlertInfo(null);
            }
          } else {
            setAlertInfo(null);
          }
        }}
      />

      <LimitAlert
        open={showLimitPage}
        closeAction={(isOk) => {
          setShowLimitPage(false);

          if (isOk) {
            ga_logEvent(EventName.GA_search_now_limit_notify_clicked);
            setTimeout(() => {
              gotoPlanClick();
            }, 20);
          }
        }}
      />

      <AccountLimitAlert
        open={showAccountLimit}
        closeAction={(isOk) => {
          setShowAccountLimit(false);

          if (isOk) {
            ga_logEvent(EventName.GA_account_limit_reached_notify_clicked);
            setTimeout(() => {
              gotoPlanClick();
            }, 20);
          }
        }}
      />
      <TurnstileModal ref={turnstileRef} />
    </>
  );
};

export default DownloadInstagramStoryContent;
